import React, { type MouseEventHandler, useMemo } from "react";

import cn from "classnames";

import { AVA_PICTURE_DISPLAY_NAME } from "./utils/core";
import { Box, boxForwardRef } from "../../helpers/common/Box/Box";
import { useMouseDownAnimation } from "../../hooks/useMouseDownAnimation";
import { getChildrenRecord } from "../../utils/react";
import { UniBoxAdditional } from "../UniBox";

import type { AvaPictureProps } from "./types";
import type { BoxComponentWithDefault } from "../../helpers/common/Box/Box";

import classes from "./AvaPicture.styl";

export const AVA_PICTURE_TEST_ID = "ava-picture-test-id";

/**
 * Компонент для отображения картинки или фигуры с контентом в виде определённой формы.<br>
 * Поддерживает обработку состояний
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-89516" target="_blank">Спека</a>
 * <br><br>
 * ### Дочерние компоненты
 * В родительский компонент <b>обязательно должен быть передан один из следующих дочерних, отвечающих за наполнение родителя: `Image`, `Initials`, `Count`, `Icon`, `Emoji`.</b><br />
 * Интерфейсы дочерних компонентов можно посмотреть в соответствующих стори.
 * | Компонент | Использование |
 * |-----------|----------|
 * | `<AvaPicture.Image />` | Изображение, имеющее внутреннюю обводку. До загрузки основного изображения или при невалидном `src` отображается картинка-плейсхолдер. |
 * | `<AvaPicture.Initials />` | Компонент для отображения инициалов, макс. кол-во символов: 2. |
 * | `<AvaPicture.Count />` | Счётчик с заранее определёнными правилами отображения значения. |
 * | `<AvaPicture.Icon />` | Компонент для отображения иконки. |
 * | `<AvaPicture.Emoji />` | Компонент для отображения эмодзи. |
 * | `<AvaPicture.Gift />` | Компонент-аддон, предназначенный для использования внутри `<AvaPicture.SlotBottomRight />`. |
 * ### Слоты для аддонов
 * Слоты — компоненты с предустановленными отступами и размерами для всех вариантов `size` и `shape`.<br />
 * Если вам нужны свои слоты, надо самостоятельно прокинуть в `<AvaPicture />` компоненты `<UniBox />` или `<UniBoxAdditional />`.
 * | Слот | Описание |
 * |-----------|----------|
 * | `<AvaPicture.SlotTopRight />` | Компонент, принимающий в себя `<Indicator />` или `<IndicatorCounter />.` |
 * | `<AvaPicture.SlotBottomRight />` | Компонент, принимающий в себя `<Badge />` или `<AvaPicture.Gift />`. |
 * | `<AvaPicture.SlotBottom />` | Компонент, принимающий в себя `<Tag />`. |
 * | `<AvaPicture.SlotBottomLeft />` | Компонент, принимающий в себя `<OnlineIndicatorWeb />` или `<OnlineIndicatorMob />`. |
 * @constructor
 */
export const AvaPicture = boxForwardRef<
    BoxComponentWithDefault<"button", AvaPictureProps, "className">
>(
    (
        {
            as = "button",
            size = "56",
            shape = "circle",
            state = "enabled",
            border = false,
            outline = false,
            children,
            onMouseDown,
            customVarsStyles,
            "data-tsid": tsid,
            ...props
        },
        ref,
    ) => {
        const { clicked, mousedownHandler } = useMouseDownAnimation();

        const {
            Initials,
            Count,
            Icon,
            Image,
            Emoji,
            SlotTopRight,
            SlotBottom,
            SlotBottomRight,
            SlotBottomLeft,
            ...otherChildren
        } = useMemo(
            () =>
                getChildrenRecord(children, AVA_PICTURE_DISPLAY_NAME, {
                    Initials: {
                        props: { size, shape },
                        optionalRequired: true,
                    },
                    Count: {
                        props: { size, shape },
                        optionalRequired: true,
                    },
                    Icon: {
                        props: { size, shape },
                        optionalRequired: true,
                    },
                    Image: {
                        props: { size, shape },
                        optionalRequired: true,
                    },
                    Emoji: {
                        props: { size, shape },
                        optionalRequired: true,
                    },
                    SlotTopRight: {
                        props: { size, shape },
                    },
                    SlotBottom: {
                        props: { size },
                    },
                    SlotBottomRight: {
                        props: { size, shape },
                    },
                    SlotBottomLeft: {
                        props: { size, shape },
                    },
                }),
            [children, size, shape],
        );

        const wrapperClasses = cn(
            classes["wrapper"],
            classes[`__size-${size}`],
            classes[`__shape-${shape}`],
            state !== "enabled" && classes[`__state-${state}`],
            {
                [classes["__border"]]: border,
                [classes["__outline"]]: outline,
                [classes["__clicked"]]: clicked && state === "enabled",
            },
            customVarsStyles,
        );

        const avaPictureClasses = cn(
            classes["ava-picture"],
            classes[`__size-${size}`],
            classes[`__shape-${shape}`],
            state !== "enabled" && classes[`__state-${state}`],
        );

        const mouseDownHandler: MouseEventHandler<HTMLButtonElement> = (e) => {
            onMouseDown?.(e);
            mousedownHandler();
        };

        const isNonInteractive = state === "none";
        // у тега <a> нет атрибута disabled, поэтому поместим в as={div}
        const isDisabledLink = state === "disabled" && as === "a";
        const elementType = isNonInteractive || isDisabledLink ? "div" : as;

        const interactionProps = isNonInteractive
            ? { tabIndex: -1 }
            : {
                  onMouseDown: mouseDownHandler,
                  disabled: state === "disabled",
              };

        return (
            <div
                data-tsid={tsid || AVA_PICTURE_TEST_ID}
                data-uikit={AVA_PICTURE_DISPLAY_NAME}
                className={wrapperClasses}
            >
                <Box
                    as={elementType}
                    {...props}
                    {...interactionProps}
                    className={avaPictureClasses}
                    ref={ref}
                >
                    {Initials}
                    {Count}
                    {Icon}
                    {Image}
                    {Emoji}
                    <div className={classes["unibox-wrapper"]}>
                        {(SlotTopRight ||
                            SlotBottom ||
                            SlotBottomRight ||
                            SlotBottomLeft) && (
                            <UniBoxAdditional data-tsid="">
                                {SlotTopRight}
                                {SlotBottom}
                                {SlotBottomRight}
                                {SlotBottomLeft}
                            </UniBoxAdditional>
                        )}
                        {Object.values(otherChildren)}
                    </div>
                </Box>
            </div>
        );
    },
);

export default AvaPicture;
